// ===== Webhooks de saída (T3.8, RF-PRF-11) =====
//
// Eventos do outbox entregues às URLs das assinaturas da empresa, assinados com HMAC-SHA256 (`t=<unix>,v1=<hex>` sobre
// `"<t>.<corpo>"`), com retentativas, desativação depois de falhas seguidas e proteção contra SSRF: só https na porta
// padrão, sem credenciais, sem nomes internos e todo IP (literal ou resolvido na entrega) precisa ser público.

import { sha256Bytes } from "./sha256";

/** Eventos assináveis (04-api §3) que já saem pelo outbox. */
export const WEBHOOK_EVENTS = [
  "contact.created",
  "contact.updated",
  "product.created",
  "product.updated",
  "stock.changed",
  "sales_order.created",
  "sales_order.updated",
  "sales_order.status_changed",
  "purchase_order.created",
  "purchase_order.updated",
  "purchase_order.status_changed",
  "purchase_order.received",
  "invoice.authorized",
  "invoice.rejected",
  "invoice.canceled",
  "title.created",
  "title.updated",
  "title.paid",
  "title.canceled",
] as const;
export type WebhookEvent = (typeof WEBHOOK_EVENTS)[number];
const EVENT_SET: ReadonlySet<string> = new Set(WEBHOOK_EVENTS);
export const isWebhookEvent = (s: string): s is WebhookEvent => EVENT_SET.has(s);

const PREFIX_MODULE: Readonly<Record<string, string>> = {
  contact: "contacts",
  product: "products",
  stock: "stock",
  sales_order: "sales_orders",
  purchase_order: "purchase_orders",
  invoice: "invoices",
};

/**
 * Módulo cuja leitura o evento exige (assinar e receber). Título: a receber (`R`) ou a pagar (`P`) pelo lado do
 * payload; sem lado conhecido = null (não entrega).
 */
export function webhookEventModule(event: string, payload: { side?: unknown }): string | null {
  if (!isWebhookEvent(event)) return null;
  const prefix = event.slice(0, event.indexOf("."));
  if (prefix === "title")
    return payload.side === "R" ? "receivables" : payload.side === "P" ? "payables" : null;
  return PREFIX_MODULE[prefix]!;
}

/** Módulos que podem ser exigidos para assinar um evento (título: basta um dos lados). */
export function webhookEventModules(event: WebhookEvent): string[] {
  return event.startsWith("title.") ? ["receivables", "payables"] : [webhookEventModule(event, {})!];
}

// ---------- HMAC-SHA256 síncrono (sobre o SHA-256 do domínio) ----------

const enc = new TextEncoder();
const hexToBytes = (hex: string) => Uint8Array.from(hex.match(/../g)!, (h) => parseInt(h, 16));
const concat = (a: Uint8Array, b: Uint8Array) => {
  const out = new Uint8Array(a.length + b.length);
  out.set(a);
  out.set(b, a.length);
  return out;
};

export function hmacSha256Hex(key: string | Uint8Array, message: string): string {
  let k = typeof key === "string" ? enc.encode(key) : key;
  if (k.length > 64) k = hexToBytes(sha256Bytes(k));
  const block = new Uint8Array(64);
  block.set(k);
  const ipad = block.map((b) => b ^ 0x36);
  const opad = block.map((b) => b ^ 0x5c);
  const inner = hexToBytes(sha256Bytes(concat(ipad, enc.encode(message))));
  return sha256Bytes(concat(opad, inner));
}

/** Tolerância do carimbo de tempo contra replay (dos dois lados). */
export const WEBHOOK_TOLERANCE_S = 300;

export const webhookSignatureHeader = (secret: string, timestampS: number, body: string): string =>
  `t=${timestampS},v1=${hmacSha256Hex(secret, `${timestampS}.${body}`)}`;

const safeEqual = (a: string, b: string) => {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
};

/** Conferência do lado de quem recebe (documentada e usada nos testes). */
export function verifyWebhookSignature(
  secret: string,
  header: string,
  body: string,
  nowS: number,
  toleranceS = WEBHOOK_TOLERANCE_S,
): boolean {
  const parts = new Map(
    header.split(",").map((p) => {
      const i = p.indexOf("=");
      return [p.slice(0, i).trim(), p.slice(i + 1).trim()] as const;
    }),
  );
  const t = Number(parts.get("t"));
  const v1 = parts.get("v1");
  if (!Number.isInteger(t) || !v1) return false;
  if (Math.abs(nowS - t) > toleranceS) return false;
  return safeEqual(v1, hmacSha256Hex(secret, `${t}.${body}`));
}

export function generateWebhookSecret(
  random: (n: number) => Uint8Array = (n) => crypto.getRandomValues(new Uint8Array(n)),
) {
  const bin = String.fromCharCode(...random(32));
  return `whsec_${btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")}`;
}

// ---------- tentativas ----------

export const WEBHOOK_MAX_ATTEMPTS = 8;
/** Atraso antes da tentativa n+1 depois de n tentativas (1 min, 5 min, 30 min, 2 h, 6 h, 12 h, 12 h). */
const RETRY_DELAYS_S = [60, 300, 1800, 7200, 21_600, 43_200, 43_200];
export function webhookRetryDelayS(attemptsDone: number): number | null {
  if (attemptsDone <= 0) return 0;
  if (attemptsDone >= WEBHOOK_MAX_ATTEMPTS) return null;
  return RETRY_DELAYS_S[attemptsDone - 1]!;
}
export const WEBHOOK_DISABLE_STREAK = 20;
export const webhookShouldDisable = (failureStreak: number) => failureStreak >= WEBHOOK_DISABLE_STREAK;

/** 2xx = entregue; 410 Gone = o destino não quer mais (desativa); o resto (inclusive 3xx e falha de rede) repete. */
export function webhookAttemptOutcome(status: number | null): "delivered" | "retry" | "gone" {
  if (status !== null && status >= 200 && status < 300) return "delivered";
  if (status === 410) return "gone";
  return "retry";
}

export const WEBHOOK_MAX_BODY_BYTES = 64 * 1024;
export const WEBHOOK_TIMEOUT_MS = 10_000;
export const WEBHOOK_RESPONSE_SNIPPET = 1024;
export const MAX_WEBHOOKS_PER_COMPANY = 10;
export const WEBHOOK_URL_MAX = 2000;

// ---------- SSRF ----------

function ipv4Octets(s: string): number[] | null {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(s);
  if (!m) return null;
  const o = m.slice(1).map(Number);
  return o.every((x) => x <= 255) ? o : null;
}

function publicV4(o: number[]): boolean {
  const [a, b, c] = o as [number, number, number, number];
  if (a === 0 || a === 10 || a === 127 || a >= 224) return false;
  if (a === 100 && b >= 64 && b <= 127) return false;
  if (a === 169 && b === 254) return false;
  if (a === 172 && b >= 16 && b <= 31) return false;
  if (a === 192 && b === 168) return false;
  if (a === 192 && b === 0 && (c === 0 || c === 2)) return false;
  if (a === 192 && b === 88 && c === 99) return false;
  if (a === 198 && (b === 18 || b === 19)) return false;
  if (a === 198 && b === 51 && c === 100) return false;
  if (a === 203 && b === 0 && c === 113) return false;
  return true;
}

/** IPv6 em 8 grupos de 16 bits (aceita `::` e IPv4 no fim); null = inválido. */
function ipv6Groups(input: string): number[] | null {
  let s = input;
  if (!/^[0-9a-f:.]+$/i.test(s) || !s.includes(":")) return null;
  const lastColon = s.lastIndexOf(":");
  const last = s.slice(lastColon + 1);
  if (last.includes(".")) {
    const v4 = ipv4Octets(last);
    if (!v4) return null;
    s = `${s.slice(0, lastColon + 1)}${((v4[0]! << 8) | v4[1]!).toString(16)}:${((v4[2]! << 8) | v4[3]!).toString(16)}`;
  }
  const halves = s.split("::");
  if (halves.length > 2) return null;
  const parse = (x: string) => (x === "" ? [] : x.split(":"));
  const left = parse(halves[0]!);
  const right = halves.length === 2 ? parse(halves[1]!) : [];
  if ([...left, ...right].some((g) => !/^[0-9a-f]{1,4}$/i.test(g))) return null;
  const total = left.length + right.length;
  if (halves.length === 1 ? total !== 8 : total > 7) return null;
  return [
    ...left.map((g) => parseInt(g, 16)),
    ...new Array<number>(8 - total).fill(0),
    ...right.map((g) => parseInt(g, 16)),
  ];
}

function publicV6(g: number[]): boolean {
  const v4of = (hi: number, lo: number) => [hi >> 8, hi & 0xff, lo >> 8, lo & 0xff];
  // ::ffff:a.b.c.d (mapeado) e 64:ff9b::/96 (NAT64): vale o IPv4 de dentro.
  if (g.slice(0, 5).every((x) => x === 0) && g[5] === 0xffff) return publicV4(v4of(g[6]!, g[7]!));
  if (g[0] === 0x64 && g[1] === 0xff9b && g.slice(2, 6).every((x) => x === 0))
    return publicV4(v4of(g[6]!, g[7]!));
  const first = g[0]!;
  // Só unicast global (2000::/3), menos documentação, 6to4, Teredo/IETF e ORCHID.
  if ((first & 0xe000) !== 0x2000) return false;
  if (first === 0x2001 && g[1] === 0x0db8) return false;
  if (first === 0x2002) return false;
  if (first === 0x2001 && g[1]! < 0x0200) return false;
  return true;
}

/** IP (v4 ou v6, sem colchetes) roteável na internet pública. Qualquer coisa inválida = false. */
export function isPublicIp(ip: string): boolean {
  const v4 = ipv4Octets(ip);
  if (v4) return publicV4(v4);
  const v6 = ipv6Groups(ip);
  return v6 ? publicV6(v6) : false;
}

export type WebhookUrlCheck =
  | { ok: true; url: string; host: string }
  | {
      ok: false;
      reason:
        | "invalid_url"
        | "too_long"
        | "https_required"
        | "port_not_allowed"
        | "credentials_not_allowed"
        | "fragment_not_allowed"
        | "host_not_allowed"
        | "ip_not_allowed";
    };

const BLOCKED_SUFFIXES = [".localhost", ".local", ".internal", ".lan", ".home.arpa", ".intranet", ".corp"];

/**
 * Validação estática da URL (no cadastro e de novo em cada entrega). O WHATWG URL já normaliza IPv4 em decimal,
 * hexadecimal e octal (`https://2130706433/` → `127.0.0.1`), então o IP literal é conferido depois do parse.
 */
export function checkWebhookUrl(raw: string): WebhookUrlCheck {
  if (raw.length > WEBHOOK_URL_MAX) return { ok: false, reason: "too_long" };
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return { ok: false, reason: "invalid_url" };
  }
  if (u.protocol !== "https:") return { ok: false, reason: "https_required" };
  if (u.port !== "") return { ok: false, reason: "port_not_allowed" };
  if (u.username || u.password) return { ok: false, reason: "credentials_not_allowed" };
  if (u.hash || raw.includes("#")) return { ok: false, reason: "fragment_not_allowed" };
  const host = u.hostname
    .toLowerCase()
    .replace(/^\[|\]$/g, "")
    .replace(/\.$/, "");
  if (ipv4Octets(host) || host.includes(":"))
    return isPublicIp(host) ? { ok: true, url: u.toString(), host } : { ok: false, reason: "ip_not_allowed" };
  if (host === "localhost" || !host.includes(".") || BLOCKED_SUFFIXES.some((s) => host.endsWith(s)))
    return { ok: false, reason: "host_not_allowed" };
  return { ok: true, url: u.toString(), host };
}
